import React, { useContext, memo, useEffect, useRef, useState, useMemo } from "react";
import {
  Polygon
} from "@react-google-maps/api";
import axios from "axios";
import { toast } from "react-toastify";
import { WebMapContext } from "../WebMapGoogleMap";
import { SEARCH_AREA_STATES, SupplyType, bestSupplyTypeMapper, formatSupplyLevel, supplyDataMapper, supplyLvlMapper } from "../../../../constants/WebMap";
import { convertArrayToStringWithDelimeter, convertGeohashToPolygon } from "../../../../utils/helper";
import { getBestOperatorGeohashes, getBestOperatorTooltipData, getCustomOperatorGeohashes, getOperatorTooltipData } from "../../../../services/api/mapGeohashes.api";
import { getUniqueGeohashes, paginatedApiInvoker } from "../../../../utils/webMapApiHelper";
import tinygradient from "tinygradient";
import { mapValueToRange } from "../../../../helpers/fuctionHelpers";

const SupplyGeohashSection = ({
  mapTool,
  searchAreaBtn,
  setSearchAreaBtn,
  setTooltipData,
  tooltipData,
  setApisOngoing,
  saveLastBound,
  permittedOperators,
  bestOperatorsPermitted
}) => {
  let cancelToken;
  let timeOutForApiCall = 600;
  const customOpGeohashTO = useRef();
  const bestOpGeohashTO = useRef();

  const {
    eyeIcons,
    operatorDetails,
    supplyStrengthLevels,
    supplyFilters,
    mapZoomLevel,
    supplyLvlFilters,
    appliedFilters,
    geohashZoom,
    boundsChanged,
    getBbox,
    geohashNE,
    geohashSW,
    downloadData,
    setDownloadData,
    minMaxSupplyOp,
  } = useContext(WebMapContext);

  const [bestOperatorGeohashes, setBestOperatorGeohashes] = useState([]);
  const [customOperatorGeohashes, setCustomOperatorGeohashes] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const permitetdOpCodes = permittedOperators?.map((op) => op?.operator) || [];

  useEffect(() => {
    if (geohashZoom && mapZoomLevel < geohashZoom) resetData();
  }, [mapZoomLevel, geohashZoom]);

  useEffect(() => {
    if (!eyeIcons.supply) resetData();
  }, [eyeIcons.supply]);

  useEffect(() => {
    if (!supplyFilters.isBestOperator)
    resetData();
  }, [supplyLvlFilters]);

  useEffect(() => {
    if (common_cond_best) {
      fetchBestOpGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [
    geohashSW,
    eyeIcons.supply,
    appliedFilters.level,
    appliedFilters.levelVal,
    appliedFilters.quarter,
    appliedFilters.year,
    supplyFilters.isBestOperator,
    supplyFilters.aggrType.best,
    supplyFilters.dataType.best,
    supplyFilters.connectionType.best,
    geohashZoom,
    boundsChanged
  ]);

  useEffect(() => {
    if (common_cond_best && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      fetchBestOpGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  useEffect(() => {
    if (common_cond_cust) {
      fetchCustomOpGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [
    geohashSW,
    eyeIcons.supply,
    appliedFilters.level,
    appliedFilters.levelVal,
    supplyFilters.isBestOperator,
    supplyFilters.operator,
    supplyFilters.aggrType.custom,
    supplyFilters.dataType.custom,
    supplyFilters.connectionType.custom,
    supplyFilters.supplyType,
    supplyLvlFilters,
    geohashZoom,
    boundsChanged
  ]);
  
  useEffect(() => {
    if (common_cond_cust && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      fetchCustomOpGeohashesVP();
      saveLastBound(); // save data loaded bounds
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  const common_cond_best = useMemo(() => {
    return eyeIcons.supply &&
      geohashZoom && mapZoomLevel >= geohashZoom &&
      supplyFilters.isBestOperator &&
      supplyFilters.connectionType.best &&
      supplyFilters.dataType.best;
  }, [eyeIcons.supply, geohashZoom, mapZoomLevel, supplyFilters]);

  const common_cond_cust = useMemo(() => {
    if(supplyFilters.supplyType === "RSRP" || supplyFilters.supplyType === "Download" || supplyFilters.supplyType === "Upload"){
      return eyeIcons.supply &&
      geohashZoom && mapZoomLevel >= geohashZoom &&
      !supplyFilters.isBestOperator &&
      supplyFilters.connectionType.custom &&
      supplyFilters.dataType.custom &&
      supplyLvlFilters[supplyFilters.supplyType]?.length > 0;
    }else{
      return eyeIcons.supply &&
      geohashZoom && mapZoomLevel >= geohashZoom &&
      !supplyFilters.isBestOperator &&
      supplyFilters.connectionType.custom &&
      supplyFilters.dataType.custom;
    }

  }, [eyeIcons.supply, geohashZoom, mapZoomLevel, supplyFilters, supplyLvlFilters]);

  const resetData = () => {
    setBestOperatorGeohashes([]);
    setCustomOperatorGeohashes([]);
    setResetKey(prevKey => prevKey + 1);
  }

  const isNoDataOn = useMemo(() => {
    return supplyLvlFilters[supplyFilters.supplyType]?.find((x) => x.filterName === "No Data")?.isChecked;
  }, [supplyLvlFilters, supplyFilters.supplyType]);

  const fetchBestOpGeohashesVP = async () => {
    resetData();
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(bestOpGeohashTO.current);

    bestOpGeohashTO.current = setTimeout(async () => {
      try {
        const { neBound, swBound } = getBbox;    
        const payload = {
          ...appliedFilters,
          level: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
          levelVal: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null,
          northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_"),
          ct: supplyFilters.connectionType.best,
          dt: supplyFilters.dataType.best,
          ...(supplyFilters.aggrType.best? {at:supplyFilters.aggrType.best} : null)
        }
        await paginatedApiInvoker(
          (pageSize, page) => getBestOperatorGeohashes(payload, cancelToken, pageSize, page),
          30000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setBestOperatorGeohashes(prevState => [...getUniqueGeohashes([...prevState, ...data])]);
          },
          () => {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              supplyBestOperator: {
                ...payload,
                cancelToken: cancelToken,
              },
            });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log(err);
      }
    }, timeOutForApiCall);
  };

  const fetchCustomOpGeohashesVP = async () => {
    resetData();
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(customOpGeohashTO.current);

    customOpGeohashTO.current = setTimeout(async () => {
      try {
        let appliedLevelFilters = supplyLvlFilters[supplyFilters.supplyType]
          .map(({ key, isChecked }) => {
            if (isChecked) {
              return key;
            } else return null;
          })
          .filter((item) => item);
        const { neBound, swBound } = getBbox;
        const payload = {
          ...appliedFilters,
          operator: supplyFilters.operator,
          filter: convertArrayToStringWithDelimeter(appliedLevelFilters, "_"),
          ...(supplyLvlMapper[supplyFilters.supplyType]? {type:supplyLvlMapper[supplyFilters.supplyType]} : null),
          ct: supplyFilters.connectionType.custom,
          dt: supplyFilters.dataType.custom,
          ...(supplyFilters.aggrType.custom? {at:supplyFilters.aggrType.custom} : null),
          northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
          southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_"),
          level: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.level : null,
          levelVal: appliedFilters.level !== 'geoHashLevel' ? appliedFilters.levelVal : null
        }

        await paginatedApiInvoker(
          (pageSize, page) => getCustomOperatorGeohashes(payload, cancelToken, pageSize, page
          ),
          30000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setCustomOperatorGeohashes(prevState => [...getUniqueGeohashes([...prevState, ...data])]);
          },
          () => {
            //Saving filters for download
            setDownloadData({
              ...downloadData,
              supplyOperatorPerformance: {
                ...payload,
                cancelToken: cancelToken,
              },
            });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log(err);
      }
    }, timeOutForApiCall);
  };

const onClickBestOperatorGeohash = async (bestOpData) => {
    if (!tooltipData || tooltipData?.geohash !== bestOpData.geohash) {
      try {

        let aggrType = supplyFilters.aggrType.best;
        const dataTransmitMetrics = [SupplyType.DATA_RECEIVED, SupplyType.DATA_TRANSCEIVED, SupplyType.DATA_TRANSMIT];
        if (dataTransmitMetrics.includes(supplyFilters.supplyType)) aggrType = "Mean";
        
        const response = await getBestOperatorTooltipData({
          country: appliedFilters.countryCode,
          geohash: bestOpData.geohash,
          quarter: `${appliedFilters.quarter} ${appliedFilters.year}`,
          ct: supplyFilters.connectionType.best,
          dt: supplyFilters.dataType.best,
          at: aggrType,
        });
        if (response.success) {
          const updatedData = response.data.map(item => {
            // Find the operator in the permittedOperators array
            const permittedOperator = bestOperatorsPermitted.find(op => op.operator === item.operator);

            if (permittedOperator) {
              // If the operator is permitted
              if (!permittedOperator.isAnonymized) {
                // Add the real name for permitted operators
                return {
                  ...item,
                  name: permittedOperator.name,
                  isAnonymized: false,
                };
              } else {
                // If the operator is anonymized, anonymize it
                const anonymized = response.anomData.find(anon => anon.operator === item.operator);
                return {
                  ...item,
                  name: anonymized ? anonymized.anonymizedName : "",
                  isAnonymized: true,
                };
              }
            } else {
              // Operator not in permittedOperators, anonymize it
              const anonymized = response.anomData.find(anon => anon.operator === item.operator);
              return {
                ...item,
                name: anonymized ? anonymized.anonymizedName : "",
                isAnonymized: true,
              };
            }
          });
          
          const polygon = convertGeohashToPolygon(bestOpData.geohash);
          setTooltipData({
            ...bestOpData,
            polygon,
            allOperators: updatedData,
            anomData: response?.anomData,
          });
        } else {
          toast.error(response.data, { theme: "colored" });
        }
      } catch (err) {
        console.log("Erro while getting tooltip data : ", err);
        toast.error(err.message, { theme: "colored" });
      }
    }
  };
  const processResponseData = (responseData, permittedOperators, anomData) => {
    const processedData = {};  // Use an object to store the processed data by key

    for (const key in responseData) {
        const operator = responseData[key].operator;
        const anonymized = anomData.find(anon => anon.operator === operator);
        const permittedOperator = permittedOperators.find(op => op.operator === operator);

        // If the operator is permitted
        if (permittedOperator) {
            if (!permittedOperator.isAnonymized) {
                // If the operator is permitted and not anonymized, use the real name
                processedData[key] = {
                    ...responseData[key],
                    name: permittedOperator.name,
                    isAnonymized: false
                };
            } else {
                // If the operator is permitted but anonymized, use the anonymized name
                processedData[key] = {
                    ...responseData[key],
                    name: anonymized ? anonymized.anonymizedName : "",
                    isAnonymized: true
                };
            }
        } else {
            // If the operator is not permitted, anonymize it
            processedData[key] = {
                ...responseData[key],
                name: anonymized ? anonymized.anonymizedName : "",
                isAnonymized: true
            };
        }
    }

    return processedData;
};

  const onClickCustomOperatorGeohash = async (operatorData) => {
    if (!tooltipData || tooltipData?.geohash !== operatorData.geohash) {
      try {
        const response = await getOperatorTooltipData({
          country: appliedFilters.countryCode,
          geohash: operatorData.geohash,
          quarter: `${appliedFilters.quarter} ${appliedFilters.year}`,
          ct: supplyFilters.connectionType.custom,
          dt: supplyFilters.dataType.custom,
          at: supplyFilters.aggrType.custom,
        });
        if (response.success) {
          const processedResponseData = processResponseData(response.data, permittedOperators, response.anomData);
          const polygon = convertGeohashToPolygon(operatorData.geohash);
          setTooltipData({
            ...operatorData,
            polygon,
            bestOperators: processedResponseData,
            anomData: response?.anomData,
          });
        } else {
          toast.error(response.data, { theme: "colored" });
        }
      } catch (err) {
        console.log("Erro while getting tooltip data : ", err);
        toast.error(err.message, { theme: "colored" });
      }
    }
  };
 const gradient = useMemo(() => {
    return tinygradient([
      { color: '#B3B3B3', pos: 0 },
      { color: '#E8D166', pos: 0.5 },
      { color: '#D64550', pos: 1 }
    ]);
  }, []);

    const getBuildColor = (val) => {
          if(supplyFilters.supplyType){
            const min = parseInt(minMaxSupplyOp[supplyFilters.supplyType]?.min) || 0;
            const max = parseInt(minMaxSupplyOp[supplyFilters.supplyType]?.max) || 1;
            const norm = mapValueToRange(val, min, max);
            if (norm < 0 || norm > 1) return null;
            return gradient?.rgbAt(Number(norm));
          }
    };

  return (
    <div key={resetKey}>
      {mapZoomLevel >= geohashZoom && eyeIcons.supply ? (
        supplyFilters.isBestOperator ? (
          bestOperatorGeohashes.length > 0 ? (
            <div className="data-on-map">
              {bestOperatorGeohashes?.map((data, index) => {
                const operatorKey =
                  bestSupplyTypeMapper[supplyFilters.supplyType](data);
                const bestOprt = operatorDetails.find(
                  (op) => op.operator === operatorKey
                );

                if (!bestOprt) return null;

                const isPermittedAndNotAnonymized = bestOperatorsPermitted.some(
                  (op) => op.operator === bestOprt.operator && !op.isAnonymized
                );

                const polygonOptions = {
                  fillColor: isPermittedAndNotAnonymized
                    ? bestOprt.color
                    : "#B4B4B4",
                  fillOpacity: 0.8,
                  strokeColor: "#bdbdbd",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  clickable: !mapTool,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                };

                return (
                  <Polygon
                    key={`${index}_${supplyFilters.supplyType}_${data.geohash}_best`}
                    path={convertGeohashToPolygon(data.geohash)}
                    options={polygonOptions}
                    onClick={() => onClickBestOperatorGeohash(data)}
                  />
                );
              })}
            </div>
          ) : (
            <></>
          )
        ) : customOperatorGeohashes.length > 0 ? (
          <div className="data-on-map">
            {customOperatorGeohashes?.map((data, index) => {
              const supplyStgLvl =
                data[supplyLvlMapper[supplyFilters.supplyType]];
              const shouldDisplay =
                isNoDataOn || (supplyStgLvl && supplyStgLvl !== "No Data");
              const isDataFilters =
                Boolean(supplyDataMapper[supplyFilters.supplyType]) &&
                data[supplyDataMapper[supplyFilters.supplyType]];
              const polygonOptions = {
                fillColor: shouldDisplay
                  ? supplyStrengthLevels[formatSupplyLevel(supplyStgLvl)] ||
                    "grey"
                  : getBuildColor(
                      Number(data[supplyDataMapper[supplyFilters.supplyType]])
                    ),
                fillOpacity: 0.8,
                strokeColor: "#bdbdbd",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: !mapTool && shouldDisplay,
                draggable: false,
                editable: false,
                geodesic: false,
                zIndex: 1,
              };

              return (
                (shouldDisplay || isDataFilters) && (
                  <Polygon
                    path={convertGeohashToPolygon(data.geohash)}
                    key={`${index}_${supplyFilters.supplyType}_${data.geohash}_operator`}
                    options={polygonOptions}
                    onClick={() =>
                      shouldDisplay && onClickCustomOperatorGeohash(data)
                    }
                  />
                )
              );
            })}
            ;
          </div>
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
    </div>
  );
};

export default memo(SupplyGeohashSection);